import { useMemo } from 'react';
import { usePatientRole } from './usePatientRole';

interface PatientData {
  id: string;
  nickname: string;
  photo_url?: string | null;
  birth_date?: string | null;
  health_details?: string | null;
  notes?: string | null;
  address?: string | null;
  residence_latitude?: number | null;
  residence_longitude?: number | null;
  residence_address?: string | null;
  created_by?: string;
}

interface SanitizedPatientData extends PatientData {
  // These fields will be null for non-owners
  birth_date: string | null;
  health_details: string | null;
  address: string | null;
  residence_latitude: number | null;
  residence_longitude: number | null;
  residence_address: string | null;
}

interface UseSensitivePatientDataResult {
  /** Patient data with sensitive fields filtered based on role */
  sanitizedPatient: SanitizedPatientData | null;
  /** Whether the current user can view sensitive data (is owner) */
  canViewSensitiveData: boolean;
  /** Whether the role is still loading */
  isLoading: boolean;
}

/**
 * Hook that filters sensitive patient data based on user role.
 * Only owners can see: birth_date, health_details, address, GPS coordinates.
 * All other roles will see these fields as null.
 */
export function useSensitivePatientData(
  patient: PatientData | null | undefined
): UseSensitivePatientDataResult {
  const { isOwner, isCaregiver, isLoading } = usePatientRole(patient?.id);
  const hasAccess = isOwner || isCaregiver;

  const sanitizedPatient = useMemo<SanitizedPatientData | null>(() => {
    if (!patient) return null;

    // Owners and Caregivers can see all data
    if (hasAccess) {
      return {
        ...patient,
        birth_date: patient.birth_date ?? null,
        health_details: patient.health_details ?? null,
        address: patient.address ?? null,
        residence_latitude: patient.residence_latitude ?? null,
        residence_longitude: patient.residence_longitude ?? null,
        residence_address: patient.residence_address ?? null,
      };
    }

    // Others get sensitive fields filtered out
    return {
      ...patient,
      birth_date: null,
      health_details: null,
      address: null,
      residence_latitude: null,
      residence_longitude: null,
      residence_address: null,
    };
  }, [patient, hasAccess]);

  return {
    sanitizedPatient,
    canViewSensitiveData: hasAccess,
    isLoading,
  };
}

/**
 * Utility function to sanitize patient data without using the hook.
 * Use this when you already know the role.
 */
export function sanitizePatientData(
  patient: PatientData | null | undefined,
  hasAccess: boolean
): SanitizedPatientData | null {
  if (!patient) return null;

  if (hasAccess) {
    return {
      ...patient,
      birth_date: patient.birth_date ?? null,
      health_details: patient.health_details ?? null,
      address: patient.address ?? null,
      residence_latitude: patient.residence_latitude ?? null,
      residence_longitude: patient.residence_longitude ?? null,
      residence_address: patient.residence_address ?? null,
    };
  }

  return {
    ...patient,
    birth_date: null,
    health_details: null,
    address: null,
    residence_latitude: null,
    residence_longitude: null,
    residence_address: null,
  };
}
